import { useMemo } from "react";
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  type EntityListOfTransactionItem,
  getTransactions,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";

const SEARCH_TRANSACTIONS_QUERY_KEY = "SEARCH_TRANSACTIONS";

export const searchTransactionsKeys = {
  all: [SEARCH_TRANSACTIONS_QUERY_KEY],
  byQuery: (query?: string) => [...searchTransactionsKeys.all, query],
};

type Props = {
  searchPublicID?: string;
  limit?: number;
  nextPageLimit?: number;
  enabled?: boolean;
};

type PageParams = {
  offset: number;
  limit: number;
};

/**
 * Search transactions across ALL transactions (ignores date/status/paypoint/employee filters).
 * Only pass search query - this enables searching regardless of what filters are applied.
 */
export const useSearchTransactions = ({
  searchPublicID,
  limit = 10,
  nextPageLimit = 25,
  enabled = true,
}: Props) => {
  const queryKey = useMemo(
    () => searchTransactionsKeys.byQuery(searchPublicID),
    [searchPublicID],
  );

  return useInfiniteQuery<
    EntityListOfTransactionItem,
    unknown,
    InfiniteData<EntityListOfTransactionItem>,
    QueryKey,
    PageParams
  >({
    queryKey,
    queryFn: processResponseWithApiProblemDetails(
      ({ pageParam: { limit, offset } }) => {
        // Only pass search param, no other filters
        return getTransactions(
          offset,
          limit,
          searchPublicID,
          undefined, // No status filter
          undefined, // No dateFrom
          undefined, // No dateTo
          undefined, // No payPoint
          undefined, // No employee
        );
      },
    ),
    enabled: enabled && !!searchPublicID, // Only run when there's a search query (and not gated off by the caller)
    initialPageParam: { offset: 0, limit },
    getNextPageParam: (lastPage: EntityListOfTransactionItem | null) => {
      if (!lastPage) {
        return undefined;
      }

      const isFinalPage =
        lastPage.offset + lastPage.limit >= lastPage.totalCount;

      if (isFinalPage) {
        return undefined;
      }

      return { offset: lastPage.offset + lastPage.limit, limit: nextPageLimit };
    },
  });
};
